import _ from 'lodash';
import Joi from 'joi';
import PropTypes from 'prop-types';

import { getAffirmativeOptions, getAnomalySummaryOptions, getNoAnomalySummaryOptions, getPittingAnomalySummaryOptions, getPittingNoAnomalySummaryOptions } from '../../../utils/data';
import { matchPhrases } from '../../../utils/utilities';
import { EquipmentAnomalySummaryItem } from '../../../utils/models';

import Form from '../../common/form';
import toastService from '../../../services/toastService';


interface FormProps {
	onAdd: (summary: EquipmentAnomalySummaryItem) => void;
	initialAnomalySummary: EquipmentAnomalySummaryItem;
}

interface FormState {
	data: EquipmentAnomalySummaryItem;
	errors: object;
	loading: boolean;
	progress: number;
}

class EquipmentAnomalySummary extends Form {
	state = {
		data: {
			pipeBody: '',
			noOfPoints: 0,
			pitting: '',
			noOfPittingLocations: 0,
			flanges: '',
			fasteners: '',
			valves: '',
			corrosionUnderPipeSupport: '',
			noOfCups: 0,
			supportCondition: '',
			compositeWrapOnLine: 'no',
			others: '',
			description: '',
			extentOfCoatingBreakdown: '',
			
			pipe: '',
			guage: '',
			dish: '',
			topDish: '',
			bottomDish: '',
			nozzle: '',
			namePlate: '',
			skirt: '',
			structure: '',
			externalProtection: '',
			insulation: '',
			
			noOfPipe: 0,
			noOfGuage: 0,
			noOfDish: 0,
			noOfTopDish: 0,
			noOfBottomDish: 0,
			noOfNozzle: 0,
			noOfNamePlate: 0,
			noOfSkirt: 0,
			noOfStructure: 0,
			noOfExternalProtection: 0,
			noOfInsulation: 0,
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		pipeBody: Joi.string().min(3).required().label('Body Condition'),
		noOfPoints: Joi.number().min(0).required().label('Number of Points'),
		pitting: Joi.string().min(3).required().label('Pitting Condition'),
		noOfPittingLocations: Joi.any().when('pitting', {
			is: 'No Pitting',
			then: Joi.number().min(0).required().label('Number of Pitting Location Anomalies'),
			otherwise: Joi.number().min(1).required().label('Number of Pitting Location Anomalies'),
		}),
		flanges: Joi.string().min(3).required().label('Flanges Condition'),
		fasteners: Joi.string().min(3).required().label('Fasteners Condition'),
		valves: Joi.string().min(3).required().label('Valves Condition'),
		corrosionUnderPipeSupport: Joi.string().min(3).required().label('Corrosion Under Pipe Support (CUPS)'),
		noOfCups: Joi.number().min(0).required().label('Number of CUPS Anomalies'),
		compositeWrapOnLine: Joi.string().required().label('Service Condition'),
		supportCondition: Joi.string().required().label('Support Condition'),
		others: Joi.string().allow('').required().label('Other (If any)'),
		description: Joi.string().min(3).required().label('Description'),
		extentOfCoatingBreakdown: Joi.string().min(3).required().label('Extent of Coating Breakdown'),

		pipe: Joi.string().required().label('Pipe'),
		guage: Joi.string().required().label('Guage'),
		dish: Joi.string().required().label('Dish'),
		topDish: Joi.string().required().label('Top Dish'),
		bottomDish: Joi.string().required().label('Bottom Dish'),
		nozzle: Joi.string().required().label('Nozzle'),
		namePlate: Joi.string().required().label('Name Plate'),
		skirt: Joi.string().required().label('Skirt'),
		structure: Joi.string().required().label('Structure'),
		externalProtection: Joi.string().required().label('External Protection'),
		insulation: Joi.string().required().label('Insulation'),

		noOfPipe: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfGuage: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfDish: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfTopDish: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfBottomDish: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfNozzle: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfNamePlate: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfSkirt: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfStructure: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfExternalProtection: Joi.number().min(0).required().label('Number of Anomalies'),
		noOfInsulation: Joi.number().min(0).required().label('Number of Anomalies'),
	};

	static propTypes = {
		hasAnomalies: PropTypes.string,
		onAdd: PropTypes.func,
		useNewFormat: PropTypes.bool,
	};

	getKeyNotFound = (key: keyof typeof this.state.data) => {
		if (this.props.useNewFormat) return true;

		return NO_ANOMALY_STATUSES.includes(this.state.data[key]);
	}
	
	getPittingNotFound = () => {
		return matchPhrases(this.state.data.pitting, 'no pitting');
	};

	getWorkingConditionOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return [{ id: 'Working', name: 'Working' }, { id: 'Not in working condition', name: 'Not in working condition'}];
		}

		return [{ id: 'Working', name: 'Working' }];
	};

	renderAnomalyOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return getAnomalySummaryOptions();
		}

		return getNoAnomalySummaryOptions();
	};

	renderPittingAnomalyOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return getPittingAnomalySummaryOptions();
		}

		return getPittingNoAnomalySummaryOptions();
	};

	renderCoatingBreakdownOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return getAnomalySummaryOptions();
		}

		return getNoAnomalySummaryOptions();
	};
	
	renderSupportConditionOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return SUPPORT_CONDITION_OPTIONS;
		}

		return getNoAnomalySummaryOptions();
	};
	
	componentDidMount(){
		if (this.props.initialAnomalySummary) {
			this.setState({data: this.props.initialAnomalySummary});
		}
	}

	componentDidUpdate(prevProps: Readonly<FormProps>, prevState: Readonly<FormState>, snapshot?: any) {
		if (prevState.data.compositeWrapOnLine)
		if (prevState.data.pitting !== this.state.data.pitting) {
			const data: EquipmentAnomalySummaryItem = { ...this.state.data };

			if (this.getPittingNotFound()) data.noOfPittingLocations = 0
			else data.noOfPittingLocations = data.noOfPittingLocations || 1

			this.setState({ data });
		}

		if (this.props.useNewFormat) {
			if (!_.isEqual(prevProps.initialAnomalySummary, this.props.initialAnomalySummary)) {
				this.setState({ data: this.props.initialAnomalySummary });
			}

			return;
		}

		if (prevState.data.pipeBody !== this.state.data.pipeBody) {
			const data: EquipmentAnomalySummaryItem = { ...this.state.data };
			
			if (this.getKeyNotFound('pipeBody')) data.noOfPoints = 0;
			else data.noOfPoints = data.noOfPoints || 1;

			this.setState({ data });
		}


		if (prevState.data.others !== this.state.data.others) {
			const data: EquipmentAnomalySummaryItem = { ...this.state.data };

			if (matchPhrases(this.state.data.others, 'no')) data.description = 'NIL'
			else data.description = data.description ?? '';

			this.setState({ data });
		}

		if (prevState.data.corrosionUnderPipeSupport !== this.state.data.corrosionUnderPipeSupport) {
			const data: EquipmentAnomalySummaryItem = { ...this.state.data };

			if (this.getKeyNotFound('corrosionUnderPipeSupport')) data.noOfCups = 0;
			else data.noOfCups = data.noOfCups || 1;

			this.setState({ data });
		}
	};

	doSubmit = (): void => {
		this.props.onAdd(this.state.data);
		toastService.success('Anomaly Summary Saved Successfully!');
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'pipeBody',
							placeholder: 'Select Body Condition',
							label: 'Body Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfPoints',
							placeholder: 'Enter Number of Points',
							label: 'Number of Points Anomalies',
							disabled: this.getKeyNotFound('pipeBody'),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'pitting',
							placeholder: 'Select Pitting Condition',
							label: 'Pitting Condition',
							options: this.renderPittingAnomalyOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfPittingLocations',
							placeholder: 'Enter Number of Pitting Locations',
							label: 'Number of Pitting Location Anomalies',
							disabled: this.getPittingNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'flanges',
							placeholder: 'Select Flanges Condition',
							label: 'Flanges Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'fasteners',
							placeholder: 'Select Fasteners Condition',
							label: 'Fasteners Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'valves',
							placeholder: 'Select Valves Condition',
							label: 'Valves Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'corrosionUnderPipeSupport',
							placeholder: 'Select Corrosion Under Support (CUS)',
							label: 'Corrosion Under Support (CUS)',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfCups',
							placeholder: 'Enter Number of CUS Anomalies',
							label: 'Number of CUS Anomalies',
							disabled: this.getKeyNotFound('corrosionUnderPipeSupport'),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'supportCondition',
							placeholder: 'Select Support Condition',
							label: 'Support Condition',
							options: this.renderSupportConditionOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'compositeWrapOnLine',
							placeholder: 'Equipment / Vessel Service Condition',
							label: 'Service Condition',
							options: this.getWorkingConditionOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'extentOfCoatingBreakdown',
							placeholder: 'Select Extent of Coating Breakdown',
							label: 'Extent of Coating Breakdown',
							options: this.renderCoatingBreakdownOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'pipe',
							placeholder: 'Select Pipe Condition',
							label: 'Pipe Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfPipe',
							placeholder: 'Enter Number of Pipe Anomalies',
							label: 'Number of Pipe Anomalies',
							disabled: this.getKeyNotFound('pipe'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'guage',
							placeholder: 'Select Guage Condition',
							label: 'Guage Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfGuage',
							placeholder: 'Enter Number of Guage Anomalies',
							label: 'Number of Guage Anomalies',
							disabled: this.getKeyNotFound('guage'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'dish',
							placeholder: 'Select Dish Condition',
							label: 'Dish Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfDish',
							placeholder: 'Enter Number of Dish Anomalies',
							label: 'Number of Dish Anomalies',
							disabled: this.getKeyNotFound('dish'),
						})}
					</div>
				</div>
				
				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'topDish',
							placeholder: 'Select Top Dish Condition',
							label: 'Top Dish Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfTopDish',
							placeholder: 'Enter Number of Top Dish Anomalies',
							label: 'Number of Top Dish Anomalies',
							disabled: this.getKeyNotFound('topDish'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'bottomDish',
							placeholder: 'Select Bottom Dish Condition',
							label: 'Bottom Dish Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfBottomDish',
							placeholder: 'Enter Number of Bottom Dish Anomalies',
							label: 'Number of Bottom Dish Anomalies',
							disabled: this.getKeyNotFound('bottomDish'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'nozzle',
							placeholder: 'Select Nozzle Condition',
							label: 'Nozzle Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfNozzle',
							placeholder: 'Enter Number of Nozzle Anomalies',
							label: 'Number of Nozzle Anomalies',
							disabled: this.getKeyNotFound('nozzle'),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'namePlate',
							placeholder: 'Select Name Plate Condition',
							label: 'Name Plate Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfNamePlate',
							placeholder: 'Enter Number of Name Plate Anomalies',
							label: 'Number of Name Plate Anomalies',
							disabled: this.getKeyNotFound('namePlate'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'skirt',
							placeholder: 'Select Skirt Condition',
							label: 'Skirt Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfSkirt',
							placeholder: 'Enter Number of Skirt Anomalies',
							label: 'Number of Skirt Anomalies',
							disabled: this.getKeyNotFound('skirt'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'structure',
							placeholder: 'Select Structure Condition',
							label: 'Structure Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfStructure',
							placeholder: 'Enter Number of Structure Anomalies',
							label: 'Number of Structure Anomalies',
							disabled: this.getKeyNotFound('structure'),
						})}
					</div>
				</div>
				
				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'externalProtection',
							placeholder: 'Select External Protection Condition',
							label: 'External Protection Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfExternalProtection',
							placeholder: 'Enter Number of External Protection Anomalies',
							label: 'Number of External Protection Anomalies',
							disabled: this.getKeyNotFound('externalProtection'),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'insulation',
							placeholder: 'Select Insulation Condition',
							label: 'Insulation Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfInsulation',
							placeholder: 'Enter Number of Insulation Anomalies',
							label: 'Number of Insulation Anomalies',
							disabled: this.getKeyNotFound('insulation'),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'others',
							placeholder: 'Select Other Remark Present',
							label: 'Others (If any)',
							options: getAffirmativeOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>

					<div className="col">
						{this.renderTextArea({
							name: 'description',
							placeholder: 'Enter Description',
							label: 'Description',
							rows: '6',
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>
				</div>

				<div className="divider mt-4"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save Summary Information')}
				</div>
			</form>
		);
	}
}

const SUPPORT_CONDITION_OPTIONS = [
	{
		id: 'No Anomaly',
		name: 'No Anomaly',
	},
	{
		id: 'Anomaly',
		name: 'Anomaly',
	},
	{
		id: 'Missing Support',
		name: 'Missing Support',
	},
];

const NO_ANOMALY_STATUSES = getNoAnomalySummaryOptions().map((option) => option.id);

export default EquipmentAnomalySummary;
